import {
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  GroupsIcon,
  Select,
  SelectOptionType,
  Switch,
  Table,
  useDefaultSorting,
} from '@bp/ui-components';
import { Row } from '@tanstack/react-table';
import { FragmentType } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { BpPermissionChecker } from 'utils/PermissionChecker';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { UseGroupsTableFragment, useGroupsTable } from '../hooks/useGroupsTable';
import { GroupTableType } from '../types/types';
import styles from './_GroupsTable.module.scss';

type InstitutionGroupsTableProps = {
  query: FragmentType<typeof UseGroupsTableFragment> | undefined;
  permissions: BpPermissionChecker | null;
  loading: boolean;
  yearUuid: string;
  showSpanning: boolean;
  onYearSelect: (yearUuid: string) => void;
  onToggleSpanning: () => void;
  onNavigate: (uuid: string) => void;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string, name: string) => void;
  onSettings: (uuid: string) => void;
};

export const InstitutionGroupsTable = ({
  query,
  permissions,
  loading,
  yearUuid,
  showSpanning,
  onYearSelect,
  onToggleSpanning,
  onNavigate,
  onAdd,
  onEdit,
  onDelete,
  onSettings,
}: InstitutionGroupsTableProps) => {
  const { t } = useTranslation();
  const { schoolyears } = useContext(OrganizationConfigContext);
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const isDesktop = useIsDesktop();

  const { data, columns } = useGroupsTable({
    query,
    columns: ['name', 'admins', 'schoolYear', 'origin', 'nextcloud', 'threema', 'community'],
    onNavigate,
  });

  const { sorting, onSortingChange } = useDefaultSorting();

  const canAdd = permissions?.canCreateBpGroups();

  const canEdit = (row: Row<GroupTableType>) =>
    permissions?.canEditBpGroup({ uuid: row.original.uuid, organization: { uuid: organizationUuid } }) &&
    row.original.organizationUuid === organizationUuid;

  const canEditSettings = (row: Row<GroupTableType>) =>
    permissions?.canUpdateGroupSettings({
      uuid: row.original.uuid,
      organization: { uuid: row.original.organizationUuid },
    });

  const canDelete = permissions?.canDeleteBpGroups();

  const schoolYearOptions: SelectOptionType[] =
    schoolyears?.map(({ name, uuid }) => {
      return {
        label: name,
        value: uuid,
      };
    }) ?? [];

  const getDropdownMenuItems = (row: Row<GroupTableType>): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [];

    if (canEdit(row)) {
      items.push({
        label: t('common.edit'),
        onClick: () => onEdit(row.original.uuid),
      });
    }

    if (canEditSettings(row)) {
      items.push({
        label: t('settings.title'),
        onClick: () => onSettings(row.original.uuid),
      });
    }

    if (canDelete) {
      items.push(
        { type: 'ruler' },
        {
          label: t('delete.delete'),
          color: 'error',
          onClick: () => onDelete(row.original.uuid, row.original.name),
        },
      );
    }

    return items;
  };

  return (
    <Table<GroupTableType>
      className={styles['groups-table']}
      loading={loading}
      data={data}
      columns={columns}
      isDesktop={isDesktop}
      canScroll
      showSort
      sorting={sorting}
      onSortingChange={onSortingChange}
      showActionBar
      actionBarSettings={{
        showAddButton: canAdd,
        addButtonText: t('groups.addGroup'),
        showExpertFilter: true,
        extendedActionsRight: (
          <div className={styles.actions}>
            <Switch
              name='toggle-spanning'
              checked={showSpanning}
              onChange={onToggleSpanning}
              label={t('groups.showSchoolyearSpanning')}
              className={styles.switch}
            />
            <Select
              value={schoolYearOptions.find((y) => y.value === yearUuid)}
              options={schoolYearOptions}
              onChange={(option) => {
                onYearSelect((option as SingleValue<SelectOptionType>)?.value as string);
              }}
              name='school-year-select'
              dense
              className={styles.select}
            />
          </div>
        ),
      }}
      isOnWhite={false}
      showBorderRadius
      showShadow
      onAddClick={onAdd}
      emptyStateSettings={{ icon: <GroupsIcon />, padding: 'xl' }}
      lastColWidth='32px'
      lastCol={(row: Row<GroupTableType>) => {
        const items = getDropdownMenuItems(row);
        const disabled = items.length === 0;
        return (
          <Dropdown
            disabled={disabled}
            trigger={<Button disabled={disabled} hierarchy='ghost' icon={<DotsVerticalIcon />} />}
          >
            <DropdownMenu data={items} />
          </Dropdown>
        );
      }}
    />
  );
};
